import { useMemo } from 'react';
import { ChevronRight, Folder } from 'lucide-react';
import { folderTitle, foldersIn, isAccountRoot, pathSegments, type BrowsableFile, type FileFolderRow } from '../../../lib/file-browser';
import { useTranslation } from '../../../lib/i18n';
import { ScrollArea, Typography } from '../ui';
import { FilesBreadcrumb } from './FilesFolderNav';
import { FilesSpaceMark } from './FilesSpaceMark';
import styles from './FolderBrowseList.module.css';

// "Where should this go?" — the breadcrumb plus one level of folder rows, shared by every dialog that has
// to ask. Three of them do now (Move to…, New file, Upload), and before this each drew its own copy of the
// same rows; the third copy is what made it worth extracting rather than a fourth.
//
// The tree spans workspaces, so the FIRST level is the workspaces themselves — the agents are the same team
// in every one, so a workspace organises work rather than gating access.
export function FolderBrowseList({
  folder,
  onNavigate,
  /** Every file in the account — the tree is derived from their paths, since a folder exists only because
   *  some file's path has that segment. */
  files,
  workspaceNames,
  loading,
}: {
  folder: string;
  onNavigate: (folder: string) => void;
  files: BrowsableFile[];
  workspaceNames: Record<string, string>;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const folders: FileFolderRow[] = useMemo(() => foldersIn(files, folder), [files, folder]);

  // Through `folderTitle` like every other folder label, so the account roots read as the product's words
  // rather than the directory's own — `brain` is "agents" to a reader.
  const label = (path: string) => {
    const segments = pathSegments(path);
    return [folderTitle(segments[0], workspaceNames, t), ...segments.slice(1)].join(' / ');
  };

  return (
    <>
      <FilesBreadcrumb folder={folder} root="" onNavigate={onNavigate} workspaceNames={workspaceNames} />

      {loading ? (
        <Typography variant="p2" color="secondary" component="p">
          {t('common.loading')}
        </Typography>
      ) : folders.length === 0 ? (
        <Typography variant="p2" color="secondary" component="p">
          {folder === '' ? t('files.move.empty') : t('files.move.noSubfolders')}
        </Typography>
      ) : (
        <ScrollArea className={styles.list}>
          {folders.map((row) => {
            // THE FIRST LEVEL IS SPACES, NOT FOLDERS, and it says so. A Space has an identity — a name, an
            // icon, a team — where a folder is only a segment of a path, so drawing one with a folder glyph
            // told the reader they were the same kind of thing. They pick differently too: you know your
            // Space by its badge, and you find a folder by reading.
            //
            // The two levels stay in ONE browsing surface rather than becoming a Space step and then a
            // folder step: the common case is a Space you are already looking at, and a wizard would charge
            // it an extra click to answer a question it had already answered.
            // A Space, not merely a top-level row: `brain` and `skills` are single-segment too, and they are
            // account ROOTS — folders Symphony made, not teams. Badging them as Spaces gave them an initials
            // mark ("BR", "SK") beside real Spaces, and naming them off the path printed the directory's own
            // identifier, so the picker offered a folder called "brain".
            const isSpace = pathSegments(row.path).length === 1 && !isAccountRoot(row.path);
            return (
              // A row, not a DS Button — the same `role="button"` div the Files list's folder rows are, so
              // the two read as the same affordance and the whole row is the hit target.
              <div
                key={row.path}
                className={styles.row}
                role="button"
                tabIndex={0}
                aria-label={label(row.path)}
                onClick={() => onNavigate(row.path)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(row.path);
                  }
                }}
              >
                {isSpace ? (
                  <FilesSpaceMark id={row.name} name={workspaceNames[row.name] ?? row.name} size={20} />
                ) : (
                  <Folder size={18} aria-hidden className={styles.glyph} />
                )}
                <span className={styles.name}>{folderTitle(row.path, workspaceNames, t)}</span>
                <ChevronRight size={16} aria-hidden className={`${styles.glyph} dir-flip`} />
              </div>
            );
          })}
        </ScrollArea>
      )}
    </>
  );
}
